import React, {
  createContext, useContext, useMemo, useState, useRef, useEffect, useLayoutEffect,
  useId, useCallback, ReactNode, KeyboardEvent, ChangeEvent, MouseEvent,
} from "react"
import {
  Popover, PopoverContent, PopoverBody, PopoverTrigger, PopoverArrow, PopoverArrowProps,
  PlacementWithLogical,
  Portal, Box, BoxProps,
  Input as ChakraInput, InputProps as ChakraInputProps,
  InputGroup, InputLeftElement, InputRightElement, Button,
  Checkbox,
} from "@chakra-ui/react"
import SimpleBar from "simplebar-react"
import type { Modifier } from "@popperjs/core"
import Icon from "components/elements/Icon"
import useInitialSelectedFirst from "./useInitialSelectedFirst"

/**
 * Combobox — composable picker (popover + optional input + filterable list +
 * optional "create new" action). Compound-component API mirrors Chakra v3's
 * `Combobox` so that when we migrate to v3, the swap is mechanical at the
 * implementation level; call-site JSX stays the same.
 *
 * Multi-select (primary use case — tag picker):
 *
 *   <Combobox.Root
 *     items={tags}
 *     getItemKey={(t) => t.id}
 *     getItemLabel={(t) => t.title}
 *     multiple
 *     value={selectedTags}
 *     onChange={setSelectedTags}
 *     onCreate={(text) => createTag(text)}   // optional
 *   >
 *     <Combobox.Trigger><Button>+ Add tag</Button></Combobox.Trigger>
 *     <Combobox.Content>
 *       <Combobox.Input placeholder="Search tags..." />
 *       <Combobox.List>
 *         {(item) => (
 *           <Combobox.Item item={item}>
 *             {item.title}
 *           </Combobox.Item>
 *         )}
 *       </Combobox.List>
 *       <Combobox.Empty>
 *         {(query) => <>Create &quot;{query}&quot;</>}
 *       </Combobox.Empty>
 *     </Combobox.Content>
 *   </Combobox.Root>
 *
 * Single-select: drop `multiple`, `value` is `T | null`, popover closes on
 * select. Compose `Input` out when you don't want filtering. Omit `onCreate`
 * to disable the create path.
 */

// --- Context ---

// Footer actions (Combobox.Action, Combobox.Create) self-register with Root
// via context. Each gets an index just past the filtered-items range so the
// keyboard handler can navigate items + actions as one continuous sequence.
type ActionRecord = {
  id: string
  onSelect: () => void
  closeOnSelect: boolean
}

// Only fields consumed by subcomponents. Root keeps the rest (items,
// getItemLabel, setMultiMode) as local state — they're captured by Root's
// own closures (handleKeyDown, handleOpen, etc.) without leaking into the
// public context surface.
type ContextValue<T> = {
  filteredItems: T[]
  getItemKey: (item: T) => string
  query: string
  setQuery: (q: string) => void
  highlightedIndex: number
  setHighlightedIndex: (i: number) => void
  isOpen: boolean
  // Row click: toggles item in/out of the selection. In single-select also
  // closes. In multi-select, closes if multiMode is off (single-tap done),
  // stays open if multiMode is on.
  selectItem: (item: T) => void
  // Checkbox click (multi-select only): toggles the item AND activates
  // multiMode so all rows' checkboxes become visible. Popover stays open.
  toggleItem: (item: T) => void
  triggerCreate: () => void
  selectedKeys: Set<string>
  multiple: boolean
  multiMode: boolean
  onCreate?: (text: string) => void
  inputRef: React.RefObject<HTMLInputElement>
  listboxRef: React.RefObject<HTMLDivElement>
  contentId: string
  // aria-activedescendant for the input/listbox, derived from the currently
  // highlighted item. Lets screen readers announce option changes as the
  // user arrow-keys through the list.
  activeDescendantId: string | undefined
  // Shared keyboard handler (↑/↓/Enter/Esc). Attached to Input when it
  // exists, otherwise to the List so the popover is still navigable without
  // a search input.
  handleKeyDown: (e: KeyboardEvent<HTMLElement>) => void
  // Input registers itself so Root knows whether to auto-focus the Input
  // or the listbox on open.
  registerInput: (mounted: boolean) => void
  // Footer actions register/unregister themselves on mount/unmount. Order in
  // `actions` reflects DOM mount order (children-first useLayoutEffect).
  actions: ActionRecord[]
  registerAction: (record: ActionRecord) => void
  unregisterAction: (id: string) => void
  // Imperative close used by Action when closeOnSelect is true.
  close: () => void
  // Width of the per-row indicator slot. Read by Item to size its checkbox /
  // checkmark column consistently across siblings.
  slotWidth: number | string
  // When set, List renders a non-interactive heading row before each group
  // transition. undefined means flat list.
  groupBy?: (item: T) => string
  // Boundary index in `filteredItems`: when ≥ 0, List renders a thin
  // Divider before the item at this index, separating initially-selected
  // rows (above) from the rest (below). -1 means no divider.
  pinnedDivider: number
}

const ComboboxContext = createContext<ContextValue<unknown> | null>(null)

const POPOVER_PAD = 12

// Writes a max-height to the popper element based on the space available
// at the resolved placement. `!important` because Chakra's Popper plugin
// can overwrite inline styles after our modifier runs.
const viewportMaxHeight: Partial<Modifier<"viewportMaxHeight", Record<string, unknown>>> = {
  name: "viewportMaxHeight",
  enabled: true,
  phase: "afterWrite",
  fn({ state }) {
    if (typeof window === "undefined") return
    const popperEl = state.elements.popper as HTMLElement
    const ref = (state.elements.reference as HTMLElement).getBoundingClientRect()
    const vh = window.visualViewport?.height ?? window.innerHeight

    const available = state.placement.startsWith("top")
      ? ref.top - POPOVER_PAD
      : state.placement.startsWith("bottom")
        ? vh - ref.bottom - POPOVER_PAD
        : vh - 2 * POPOVER_PAD

    // Cap at 600 so a 280px popover on a tall viewport doesn't get a 1000px
    // popper box — keeps the arrow position stable on `top` placement and
    // avoids stretching the popper hit-test area past the inner SimpleBar.
    const clamped = Math.min(Math.max(available, 120), 600)
    popperEl.style.setProperty("max-height", `${clamped}px`, "important")
  },
}

const POPPER_MODIFIERS = [
  {
    name: "flip",
    options: {
      fallbackPlacements: ["top", "right-start", "left-start"],
      padding: POPOVER_PAD,
    },
  },
  viewportMaxHeight,
]

// Exposed as `useCombobox` below — lets consumers nested inside Combobox.Root
// read the internal state (query, close, handleKeyDown) without re-implementing
// the wiring. Required for custom inline content (e.g. our MultiValuePicker)
// that wants Combobox's keyboard nav + close behaviour.
const useCtx = <T, >(): ContextValue<T> => {
  const ctx = useContext(ComboboxContext) as ContextValue<T> | null
  if (!ctx) throw new Error("Combobox subcomponents must be rendered inside <Combobox.Root>")

  return ctx
}

// --- Root ---

type BaseRootProps<T> = {
  items: T[]
  getItemKey: (item: T) => string
  getItemLabel: (item: T) => string
  // Override the default substring filter. Return true to keep the item
  // visible in the list. Useful when some items (e.g. always-visible
  // actions) should bypass the search filter.
  filterItem?: (item: T, query: string) => boolean
  // Returns the heading string an item belongs to. When provided, items are
  // reordered into groups (preserving original order within each group) and
  // List renders a non-interactive heading row before each group transition.
  // Heading-only rows aren't keyboard-navigable — mirrors shadcn's
  // `CommandGroup heading`. Set `groupOrder` to override the default
  // first-seen order.
  groupBy?: (item: T) => string
  groupOrder?: string[]
  onCreate?: (text: string) => void
  // Fires when the popover opens or closes. Useful for resetting consumer
  // state on close, or pre-populating it on open (e.g. Seating auto-
  // navigates to the currently-assigned table on open).
  onOpenChange?: (isOpen: boolean) => void
  // Width of the per-row "indicator slot" (the column holding the
  // checkbox / checkmark / a hideCheckmark consumer-side icon). Set wider
  // when rows have larger leading icons that must occupy this column for
  // alignment — Seating uses `8` so size={8} table-shape icons fit.
  // Default `5` matches the size of Combobox.Item's checkmark icon.
  // Number / string only — responsive (array/object) values aren't
  // supported since the slot is a fixed flex-child width.
  slotWidth?: number | string
  // Where to anchor the popover relative to the trigger. Defaults to
  // "bottom" (Chakra's default). Override to "left-start" / "right-start"
  // for triggers in side panels where there's more horizontal room than
  // vertical — Seating uses this to avoid overflowing on tall lists.
  placement?: PlacementWithLogical
  // When `items` is empty and only footer actions exist, the first action
  // is highlighted by default so Enter routes to it (e.g. a Create row on
  // a no-results query). Pass `false` for consumers that render rows via
  // Combobox.Action — those rows shouldn't pre-highlight just because no
  // Input is present (the user hasn't expressed nav intent yet).
  autoHighlightFirstAction?: boolean
  // Multi-select only: when true, items that were selected at mount time
  // sort to the top of the list with a divider separating them from the
  // rest. The pinned set is frozen on first render so toggling doesn't
  // make rows hop. Skipped while a search query is active — match-order
  // matters more than recall-order then.
  pinSelected?: boolean
  children: ReactNode
}

type SingleRootProps<T> = BaseRootProps<T> & {
  multiple?: false
  value?: T | null
  // Return `false` to prevent the popover from closing after this change.
  // Useful for "navigation" items that drill into a sub-view, or for
  // disabled items that should do nothing on click.
  onChange?: (item: T | null) => boolean | void
}

type MultiRootProps<T> = BaseRootProps<T> & {
  multiple: true
  value?: T[]
  onChange?: (items: T[]) => void
}

type RootProps<T> = SingleRootProps<T> | MultiRootProps<T>

const Root = <T, >(props: RootProps<T>) => {
  const {
    items, getItemKey, getItemLabel, filterItem, groupBy, groupOrder,
    onCreate, onOpenChange,
    slotWidth = 5, placement = "bottom", autoHighlightFirstAction = true,
    pinSelected = false, children,
  } = props
  /* eslint-disable react/destructuring-assignment */
  const multiple = "multiple" in props && props.multiple === true
  const rawValue = props.value
  const rawOnChange = props.onChange
  /* eslint-enable react/destructuring-assignment */

  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [multiMode, setMultiMode] = useState(false)
  const [hasInput, setHasInput] = useState(false)
  const [actions, setActions] = useState<ActionRecord[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)
  // useId() returns ":r1:" — valid for HTML id attributes and getElementById,
  // but the colons break querySelector. Strip them so either lookup style
  // works downstream (e.g. test-library queries, or future refactors).
  const contentId = useId().replace(/:/g, "")
  const registerInput = useCallback((mounted: boolean) => setHasInput(mounted), [])
  const registerAction = useCallback((record: ActionRecord) => {
    setActions((prev) => {
      const idx = prev.findIndex((a) => a.id === record.id)
      if (idx === -1) return [...prev, record]
      const next = prev.slice()
      next[idx] = record

      return next
    })
  }, [])
  const unregisterAction = useCallback((id: string) => {
    setActions((prev) => prev.filter((a) => a.id !== id))
  }, [])

  const selectedKeys = useMemo(() => {
    const set = new Set<string>()
    if (multiple) {
      (rawValue as T[] | undefined)?.forEach((item) => set.add(getItemKey(item)))
    } else if (rawValue != null) {
      set.add(getItemKey(rawValue as T))
    }

    return set
  }, [multiple, rawValue, getItemKey])

  const searchedItems = useMemo(() => {
    let result: T[]
    if (!query) {
      result = items
    } else if (filterItem) {
      result = items.filter((item) => filterItem(item, query))
    } else {
      const q = query.toLowerCase()
      result = items.filter((item) => getItemLabel(item).toLowerCase().includes(q))
    }

    if (!groupBy) return result

    // Reorder filtered result into groups so keyboard nav (which indexes into
    // filteredItems) matches the rendered order in List. Preserves original
    // item order within each group; groupOrder controls cross-group order
    // (default: first-seen).
    const groups = new Map<string, T[]>()
    result.forEach((item) => {
      const key = groupBy(item)
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(item)
    })
    const order = groupOrder ?? Array.from(groups.keys())

    return order.flatMap((g) => groups.get(g) ?? [])
  }, [items, query, getItemLabel, filterItem, groupBy, groupOrder])

  // Pin initially-selected items to the top (one snapshot per Root instance,
  // so toggling selection mid-session doesn't reshuffle the list). Skipped
  // while a search query is active — relevance matters more than recall
  // order in that mode.
  const { items: filteredItems, divider: pinnedDivider } = useInitialSelectedFirst(
    searchedItems,
    getItemKey,
    selectedKeys,
    pinSelected && !query,
    isOpen,
  )

  const computeToggled = useCallback((item: T): T[] => {
    const v = (rawValue as T[] | undefined) ?? []
    const key = getItemKey(item)

    return v.some((i) => getItemKey(i) === key)
      ? v.filter((i) => getItemKey(i) !== key)
      : [...v, item]
  }, [rawValue, getItemKey])

  const selectItem = useCallback((item: T) => {
    let keepOpen = false
    if (multiple) {
      (rawOnChange as ((items: T[]) => void) | undefined)?.(computeToggled(item))
    } else {
      const result = (rawOnChange as ((item: T | null) => boolean | void) | undefined)?.(item)
      if (result === false) keepOpen = true
    }
    if (!multiMode && !keepOpen) setIsOpen(false)
  }, [multiple, multiMode, rawOnChange, computeToggled])

  const toggleItem = useCallback((item: T) => {
    if (!multiple) return
    (rawOnChange as ((items: T[]) => void) | undefined)?.(computeToggled(item))
    setMultiMode(true)
  }, [multiple, rawOnChange, computeToggled])

  const triggerCreate = useCallback(() => {
    if (!onCreate) return
    onCreate(query.trim())
    // Closing is handled by Combobox.Action's closeOnSelect — keeping
    // triggerCreate side-effect-free besides invoking the consumer callback.
  }, [onCreate, query])

  // Linear-style: no row is pre-highlighted on open. The user signals nav
  // intent by typing into the input or pressing ArrowDown — Enter on the
  // listbox without that intent does nothing. Applies to input-mode and
  // input-less pickers alike for behavioral consistency across consumers.
  //
  // Two exceptions:
  // - The user has typed a non-empty query → highlight the top result so
  //   Enter selects the obvious match.
  // - The list is empty but footer actions exist → highlight the first
  //   action so Enter routes to it (e.g. Create on a no-results query).
  useLayoutEffect(() => {
    if (filteredItems.length === 0 && actions.length > 0 && autoHighlightFirstAction) {
      setHighlightedIndex(0)
    } else {
      setHighlightedIndex(query ? 0 : -1)
    }
  }, [filteredItems.length, query, actions.length, autoHighlightFirstAction])

  // Focus the right thing on open and whenever Input mounts/unmounts mid-open
  // (e.g. Seating step 2 hides the Input — focus must move to the listbox so
  // keyboard nav keeps working).
  useEffect(() => {
    if (!isOpen) return undefined
    // Chakra's autoFocus is off (see Popover props below) so we manage focus
    // ourselves. requestAnimationFrame x2 reliably waits for the Popover's
    // Portal-mounted content to be in the DOM with refs attached.
    let frame2 = 0
    const frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        const target = hasInput ? inputRef.current : listboxRef.current
        target?.focus()
      })
    })

    return () => {
      window.cancelAnimationFrame(frame1)
      window.cancelAnimationFrame(frame2)
    }
  }, [isOpen, hasInput])

  // Items + actions form one continuous keyboard-navigable sequence. Indices
  // 0..items.length-1 are items; items.length..totalNav-1 are footer actions
  // in registration order.
  const totalNav = filteredItems.length + actions.length

  // Mouse hover updates the highlight (so the cursor stays in sync visually)
  // but never auto-scrolls — only keyboard nav scrolls partially-visible rows
  // into view. The flag is set by ArrowUp/ArrowDown and consumed by the
  // scroll effect below.
  const scrollNextHighlightRef = useRef(false)

  // Arrow nav doesn't skip rows marked `disabled` on the Item — disabled
  // lives at the JSX level, not in the items array, so the keydown handler
  // can't see it. Consumers gate Enter via onChange returning `false`.
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      scrollNextHighlightRef.current = true
      setHighlightedIndex(Math.min(highlightedIndex + 1, Math.max(totalNav - 1, 0)))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      scrollNextHighlightRef.current = true
      setHighlightedIndex(Math.max(highlightedIndex - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (highlightedIndex < 0) return
      if (highlightedIndex < filteredItems.length) {
        selectItem(filteredItems[highlightedIndex])
      } else {
        const action = actions[highlightedIndex - filteredItems.length]
        if (action) {
          action.onSelect()
          if (action.closeOnSelect) setIsOpen(false)
        }
      }
    } else if (e.key === "Escape") {
      e.preventDefault()
      setIsOpen(false)
    }
  }, [filteredItems, actions, highlightedIndex, selectItem])

  const activeItem = filteredItems[highlightedIndex]
  const activeAction = highlightedIndex >= filteredItems.length
    ? actions[highlightedIndex - filteredItems.length]
    : undefined
  let activeDescendantId: string | undefined
  if (activeItem) activeDescendantId = `${contentId}-option-${getItemKey(activeItem)}`
  else if (activeAction) activeDescendantId = `${contentId}-action-${activeAction.id}`

  // block:"nearest" avoids gratuitous jumps when the row is already fully
  // in view; the ref gate ensures only keyboard nav scrolls (see above).
  useEffect(() => {
    if (!scrollNextHighlightRef.current) return
    scrollNextHighlightRef.current = false
    if (!activeDescendantId) return
    const el = document.getElementById(activeDescendantId)
    el?.scrollIntoView?.({ block: "nearest" })
  }, [activeDescendantId])

  // Reset list state when the items array changes identity while the popover
  // is open (e.g. Seating step 1 → step 2, AddFilterPopover field → value).
  // Clears: scroll position, search query, highlight, and refocuses the input
  // if one is mounted — so step 2 lands in a clean state ready for typing.
  // The isOpen gate keeps the effect from firing on parent re-renders that
  // pass fresh items array literals — without it, every unrelated re-render
  // would yank scroll and wipe the user's in-progress query.
  useEffect(() => {
    if (!isOpen) return undefined
    setQuery("")
    setHighlightedIndex(-1)
    const scroller = listboxRef.current?.querySelector(".simplebar-content-wrapper")
    if (scroller instanceof HTMLElement) scroller.scrollTop = 0
    if (hasInput) {
      // Same double-rAF dance as the open-focus effect — needed so the Input
      // is in the DOM before we focus it after a step transition.
      const frame1 = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => { inputRef.current?.focus() })
      })

      return () => { window.cancelAnimationFrame(frame1) }
    }

    return undefined
  }, [items, isOpen, hasInput])

  // ctxValue is rebuilt every Root render anyway (plain object literal), so
  // memoizing `close` would just add overhead without buying stable identity.
  const close = () => setIsOpen(false)

  const ctxValue: ContextValue<T> = {
    filteredItems,
    getItemKey,
    query,
    setQuery,
    highlightedIndex,
    setHighlightedIndex,
    isOpen,
    selectItem,
    toggleItem,
    triggerCreate,
    selectedKeys,
    multiple,
    multiMode,
    onCreate,
    inputRef,
    listboxRef,
    contentId,
    activeDescendantId,
    handleKeyDown,
    registerInput,
    actions,
    registerAction,
    unregisterAction,
    close,
    slotWidth,
    groupBy,
    pinnedDivider,
  }

  // Reset state on open (not close) so the input doesn't visibly clear
  // during the close animation.
  const handleOpen = () => {
    setQuery("")
    setHighlightedIndex(-1)
    setMultiMode(false)
    setIsOpen(true)
    onOpenChange?.(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    onOpenChange?.(false)
  }

  return (
    <ComboboxContext.Provider value={ctxValue as ContextValue<unknown>}>
      <Popover
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        placement={placement}
        arrowSize={15}
        closeOnBlur
        closeOnEsc
        isLazy
        modifiers={POPPER_MODIFIERS}
      >
        {children}
      </Popover>
    </ComboboxContext.Provider>
  )
}

// --- Trigger ---

type TriggerProps = {
  children: ReactNode
}

const Trigger = ({ children }: TriggerProps) => {
  const { isOpen, contentId } = useCtx()

  // preventDefault on mousedown suppresses the focus shift that would
  // otherwise trip closeOnBlur on the second click of an open popover.
  return (
    <PopoverTrigger>
      <Box
        onMouseDown={(e) => e.preventDefault()}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={`${contentId}-listbox`}
        cursor="pointer"
        minW={0}
      >
        {children}
      </Box>
    </PopoverTrigger>
  )
}

// --- Content ---

type ContentProps = {
  children: ReactNode
  w?: BoxProps["w"]
  maxW?: BoxProps["maxW"]
  // Opt in to "grow with content up to maxW" behaviour — `w` becomes the
  // floor instead of a hard width. Off by default so existing consumers
  // keep their fixed width unchanged.
  growToContent?: boolean
  arrowBg?: PopoverArrowProps["bg"]
}

const Content = ({
  children, w = "280px", maxW, growToContent = false, arrowBg = "gray.50",
}: ContentProps) => {
  const { contentId } = useCtx()
  const widthProps = growToContent
    ? { minW: w, w: "max-content", maxW: maxW ?? "min(560px, 90vw)" }
    : { w, maxW }

  return (
    <Portal>
      <PopoverContent
        {...widthProps}
        id={contentId}
        boxShadow="lg"
        display="flex"
        flexDirection="column"
      >
        <PopoverArrow bg={arrowBg} />
        <PopoverBody
          p={0}
          overflow="hidden"
          borderRadius="md"
          display="flex"
          flexDirection="column"
          minH={0}
          flex={1}
        >
          {children}
        </PopoverBody>
      </PopoverContent>
    </Portal>
  )
}

// --- Header (optional title strip at the top of the popover) ---

type HeaderProps = {
  children: ReactNode
} & BoxProps

const Header = ({ children, ...rest }: HeaderProps) => (
  <Box
    minH={14}
    px={5}
    py={3}
    bg="gray.50"
    borderTopRadius="md"
    borderBottomWidth={1}
    borderColor="gray.200"
    fontSize="xl"
    color="black"
    display="flex"
    alignItems="center"
    justifyContent="center"
    {...rest}
  >
    {children}
  </Box>
)

// --- Input ---

type InputProps = Omit<ChakraInputProps, "value" | "onChange" | "onKeyDown" | "ref">

const Input = ({ placeholder = "Search...", ...rest }: InputProps) => {
  const {
    query, setQuery, inputRef, handleKeyDown, registerInput, activeDescendantId,
  } = useCtx()

  // Layout effect so `hasInput` is true before Root's highlight-reset effect
  // runs on the same commit — avoids a flash of the "first item highlighted"
  // state on open in input-mode pickers.
  useLayoutEffect(() => {
    registerInput(true)

    return () => registerInput(false)
  }, [registerInput])

  return (
    <Box borderBottomWidth={1} borderColor="gray.200">
      <InputGroup>
        <InputLeftElement pointerEvents="none" h="full" pl={2}>
          <Icon icon="guest-search" color="gray.500" />
        </InputLeftElement>
        {/* Default scale shared with Combobox.Action — bookends. Items
            between them use the denser `py={2}` content scale. */}
        <ChakraInput
          ref={inputRef}
          value={query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          variant="unstyled"
          pl={10}
          pr={10}
          py={3}
          h="auto"
          fontSize="md"
          autoComplete="off"
          aria-autocomplete="list"
          aria-activedescendant={activeDescendantId}
          {...rest}
        />
        {query && (
          <InputRightElement h="full" pr={2}>
            <Button
              variant="link"
              cursor="pointer"
              onClick={() => {
                setQuery("")
                inputRef.current?.focus()
              }}
            >
              <Icon icon="times" color="blue.500" />
            </Button>
          </InputRightElement>
        )}
      </InputGroup>
    </Box>
  )
}

// --- List ---

type ListProps<T> = {
  children: (item: T, index: number) => ReactNode
  maxH?: BoxProps["maxH"]
  // Raw scroll event from the inner SimpleBar viewport, for infinite-scroll
  // pagination — callers debounce and check scroll position themselves.
  onScroll?: (e: Event) => void
}

const List = <T, >({ children, maxH = "240px", onScroll }: ListProps<T>) => {
  const {
    filteredItems, getItemKey, listboxRef, handleKeyDown,
    contentId, activeDescendantId, groupBy, pinnedDivider,
  } = useCtx<T>()
  if (filteredItems.length === 0) return null

  // Outer Box is the focusable listbox container — ref needs to land on a
  // real DOM element. SimpleBar wraps a div but its React ref points to the
  // SimpleBar instance, not the DOM, which would break `listboxRef.focus()`.
  // tabIndex={-1} allows programmatic focus without entering the tab order.
  //
  // `flex={1} minH={0}` makes the list shrink within the popover's dynamic
  // max-height cap. The inner SimpleBar's `maxH` stays the upper bound.
  return (
    <Box
      ref={listboxRef}
      id={`${contentId}-listbox`}
      role="listbox"
      tabIndex={-1}
      outline="none"
      aria-activedescendant={activeDescendantId}
      onKeyDown={handleKeyDown}
      flex={1}
      minH={0}
      display="flex"
      flexDirection="column"
    >
      {/* py on the SimpleBar wrapper so the first and last rows have
          breathing room matching what the section dividers add. */}
      <Box
        as={SimpleBar}
        maxH={maxH}
        h="100%"
        flex={1}
        minH={0}
        py={1}
        scrollableNodeProps={onScroll ? { onScroll } : undefined}
      >
        {filteredItems.map((item, index) => {
          // When groupBy is set, insert a heading row before each group
          // transition. Heading is visual-only — no role="option", not
          // keyboard-navigable. Mirrors shadcn's CommandGroup heading.
          const currentGroup = groupBy?.(item)
          const prevGroup = index > 0 ? groupBy?.(filteredItems[index - 1]) : undefined
          const showHeading = !!groupBy && currentGroup !== prevGroup
          // pinSelected divider: thin separator (no heading text) before
          // the first non-pinned row. -1 disables.
          const showPinnedDivider = pinnedDivider > 0 && index === pinnedDivider

          return (
            <React.Fragment key={getItemKey(item)}>
              {showHeading && (
                <Box
                  px={3}
                  pt={index === 0 ? 2 : 3}
                  pb={1}
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  color="gray.500"
                >
                  {currentGroup}
                </Box>
              )}
              {showPinnedDivider && <Box my={1} borderTopWidth={1} borderColor="gray.200" />}
              {children(item, index)}
            </React.Fragment>
          )
        })}
      </Box>
    </Box>
  )
}

// --- Item ---

type ItemProps<T> = {
  item: T
  children: ReactNode
  // Visually disable + block mouse selection. Keyboard Enter will still
  // fire onChange — the consumer should `return false` to keep the popover
  // open (matches Seating's "taken seat" pattern).
  disabled?: boolean
  // Mark this row as the end of a section. Renders a 1px divider below the
  // row *only when there's a visible item after it* — so an orphaned section
  // ender (e.g. filter narrows to just this row) doesn't draw a hanging
  // border that stacks against the next sibling's own border.
  divideAfter?: boolean
  // Suppress the checkmark icon for this row even when it matches `value`.
  // The blue background tint still applies as the selected signal. Used when
  // a leading icon in the row (e.g. Seating's table-shape icon) would create
  // a visually busy "two icons stacked" effect with the checkmark.
  hideCheckmark?: boolean
} & Omit<BoxProps, "onClick">

const Item = <T, >({
  item, children, disabled, divideAfter, hideCheckmark, ...rest
}: ItemProps<T>) => {
  const {
    filteredItems, getItemKey, highlightedIndex, setHighlightedIndex, selectItem,
    toggleItem, selectedKeys, multiple, multiMode, contentId, slotWidth,
  } = useCtx<T>()
  const itemKey = getItemKey(item)
  const index = filteredItems.findIndex((i) => getItemKey(i) === itemKey)
  const highlighted = index === highlightedIndex
  const selected = selectedKeys.has(itemKey)
  const itemId = `${contentId}-option-${itemKey}`
  // Only draw a section divider if there's actually a row to separate from.
  // Assumes section enders are non-adjacent — two consecutive `divideAfter`
  // rows would each draw their own border, stacking into 2px.
  const showSectionDivider = divideAfter && index < filteredItems.length - 1

  // visibility:hidden (not conditional render) so the row's layout stays
  // stable as the mouse moves down the list.
  const checkboxVisible = multiple && (multiMode || highlighted || selected)
  // Single-select shows a blue tint + a left-side checkmark in the same
  // indicator slot the multi-select checkbox lives in. House convention:
  // selection signal is always on the left, so eye scans one column.
  const showSingleSelectIndicator = !multiple && selected

  const onRowClick = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled) return
    e.preventDefault()
    selectItem(item)
  }

  const bg = showSingleSelectIndicator
    ? "blue.50"
    : highlighted ? "gray.100" : "transparent"
  const hoverBg = showSingleSelectIndicator ? "blue.50" : "gray.100"

  return (
    <>
      <Box
        id={itemId}
        role="option"
        aria-selected={selected}
        aria-disabled={disabled || undefined}
        data-highlighted={highlighted ? "" : undefined}
        onMouseEnter={disabled ? undefined : () => setHighlightedIndex(index)}
        onClick={onRowClick}
        px={3}
        py={2}
        cursor={disabled ? "not-allowed" : "pointer"}
        opacity={disabled ? 0.6 : 1}
        bg={bg}
        _hover={disabled ? undefined : { bg: hoverBg }}
        display="flex"
        alignItems="center"
        gap={2}
        {...rest}
      >
        {multiple && (
        <Box
          visibility={checkboxVisible ? "visible" : "hidden"}
          display="flex"
          alignItems="center"
          justifyContent="center"
          w={slotWidth}
          flexShrink={0}
          position="relative"
          onClick={(e) => {
            e.stopPropagation()
            if (disabled) return
            toggleItem(item)
          }}
          // Forgiving hit area: extends the checkbox click target 8px past
          // the visible box on all sides so a slightly-off click still
          // toggles instead of falling through to the row's commit path.
          // Pseudo-element keeps layout neutral.
          _before={{
            content: "\"\"",
            position: "absolute",
            top: "-8px",
            right: "-8px",
            bottom: "-8px",
            left: "-8px",
          }}
        >
          <Checkbox
            isChecked={selected}
            colorScheme="blue"
            pointerEvents="none"
            tabIndex={-1}
          />
        </Box>
        )}
        {!multiple && !hideCheckmark && (
        <Box
          w={slotWidth}
          flexShrink={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {showSingleSelectIndicator && (
            <Icon icon="checkmark" color="blue.500" />
          )}
        </Box>
        )}
        {/* hideCheckmark single-select: no slot rendered. Consumer contract —
          their leading icon's width should match slotWidth so text aligns
          with the indicator column on other rows. */}
        <Box flex={1} display="flex" alignItems="center">
          {children}
        </Box>
      </Box>
      {/* Spaced divider mirrors the pinnedDivider style so divideAfter and
        pinSelected feel like the same "section break" semantic. */}
      {showSectionDivider && <Box my={1} borderTopWidth={1} borderColor="gray.200" />}
    </>
  )
}

// --- Action (keyboard-navigable footer row) ---

type ActionProps = {
  onSelect: () => void
  // Default true: popover closes after the action fires. Set to false for
  // actions that should keep the popover open (rare — most footer actions
  // either navigate away or open a modal, both of which want the popover
  // out of the way).
  closeOnSelect?: boolean
  children: ReactNode | ((query: string) => ReactNode)
  // BoxProps ships its own `onSelect` (the HTMLDivElement event handler)
  // which would collide with our custom one-arg callback. Omit it.
} & Omit<BoxProps, "onClick" | "onSelect" | "children">

/**
 * Footer action row. Self-registers with Root via context so ArrowDown can
 * navigate from the last list item into the footer, and Enter triggers the
 * action just like clicking it.
 *
 * Default `px={3} py={3}` matches Combobox.Item's horizontal padding so a
 * leading icon in the Action lands in the same column as the Item indicator
 * slot (checkbox / checkmark). Use this for "New X", "Edit X", or any other
 * action that should be both clickable and keyboard-reachable from the
 * listbox.
 */
const Action = ({
  onSelect, closeOnSelect = true, children, ...rest
}: ActionProps) => {
  const {
    query, actions, registerAction, unregisterAction,
    filteredItems, highlightedIndex, setHighlightedIndex,
    contentId, close,
  } = useCtx()

  // Strip useId's colons (see contentId in Root).
  const id = useId().replace(/:/g, "")

  // Consumers commonly pass inline arrow functions as onSelect, which gives
  // us a fresh reference on every render. Without this ref-indirection the
  // registration effect would re-fire each render → setActions → re-render
  // → infinite loop. We keep the registry pointing at `latestOnSelect`,
  // which is stable, and update the ref synchronously each render so the
  // keydown handler sees the latest closure.
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const latestOnSelect = useCallback(() => {
    onSelectRef.current()
  }, [])

  // useLayoutEffect (not useEffect) so the registration commits before the
  // highlight-reset effect in Root runs on the same commit. Avoids a flash
  // where the action exists in the DOM but isn't yet keyboard-navigable.
  useLayoutEffect(() => {
    registerAction({ id, onSelect: latestOnSelect, closeOnSelect })

    return () => unregisterAction(id)
  }, [id, latestOnSelect, closeOnSelect, registerAction, unregisterAction])

  const actionIndex = actions.findIndex((a) => a.id === id)
  const navIndex = filteredItems.length + actionIndex
  const highlighted = actionIndex >= 0 && navIndex === highlightedIndex
  const actionId = `${contentId}-action-${id}`

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    onSelectRef.current()
    if (closeOnSelect) close()
  }

  const trimmed = query.trim()
  const content = typeof children === "function" ? children(trimmed) : children

  return (
    <Box
      as="button"
      type="button"
      role="option"
      id={actionId}
      aria-selected={highlighted}
      data-highlighted={highlighted ? "" : undefined}
      onClick={handleClick}
      onMouseEnter={() => setHighlightedIndex(navIndex)}
      w="full"
      textAlign="left"
      px={3}
      // Bookend scale shared with Combobox.Input. Row-style consumers
      // (MultiValuePicker) override py=0 and size their inner Flex.
      py={3}
      cursor="pointer"
      bg={highlighted ? "gray.100" : "transparent"}
      _hover={{ bg: "gray.100" }}
      display="flex"
      alignItems="center"
      {...rest}
    >
      {content}
    </Box>
  )
}

// --- Create (specialized Action for "Create new X") ---

type CreateProps = {
  children: ReactNode | ((query: string) => ReactNode)
} & Omit<BoxProps, "onClick" | "onSelect" | "children">

/**
 * "Create new X" footer action. Thin wrapper around Combobox.Action — fires
 * Root.onCreate(query) on activation. Default `blue.400` matches the
 * menu-action house style.
 *
 * Border policy: 1px top border only when there are items above. When the
 * list is empty, the Input or Header's bottom border serves as the separator.
 */
const Create = ({ children, ...rest }: CreateProps) => {
  const { onCreate, triggerCreate, filteredItems } = useCtx()
  if (!onCreate) return null

  const hasItemsAbove = filteredItems.length > 0

  return (
    <Action
      onSelect={triggerCreate}
      color="blue.400"
      borderTopWidth={hasItemsAbove ? 1 : 0}
      borderColor="gray.200"
      {...rest}
    >
      {children}
    </Action>
  )
}

// --- Empty (rendered only when filteredItems is empty) ---

type EmptyProps = {
  children: ReactNode | ((query: string) => ReactNode)
} & Omit<BoxProps, "children">

/**
 * Renders only when the filtered list is empty — gives consumers a slot for
 * a "no results" message + optional escape hatches (e.g. a link to a settings
 * page where the user can manage the underlying data).
 *
 * Border policy: Empty draws no border of its own. The Input (or Header) above
 * already owns a bottom border that acts as the visual separator. Don't paste
 * a `borderTopWidth={1}` reflex here — it stacks against the Input's border.
 */
const Empty = ({ children, ...rest }: EmptyProps) => {
  const { filteredItems, query } = useCtx()
  if (filteredItems.length > 0) return null

  const trimmed = query.trim()
  const content = typeof children === "function" ? children(trimmed) : children

  return <Box {...rest}>{content}</Box>
}

// --- Public API ---

export const useCombobox = useCtx

export const Combobox = {
  Root,
  Trigger,
  Content,
  Header,
  Input,
  List,
  Item,
  Action,
  Create,
  Empty,
}

export default Combobox
